import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  Stack,
  Alert,
  Box,
  Divider,
  Snackbar,
} from "@mui/material";
import {
  CheckCircle,
  Schedule,
  Error,
  Download,
  WhatsApp,
  ArrowBack,
} from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toBlob } from "html-to-image";
import CompanyLogo from "../../components/CompanyLogo";

/**
 * Simple & Professional Receipt Component
 * - Clean, minimalist design
 * - Easy to read layout
 * - Professional appearance
 * - Optimized for print/PDF
 */
export default function Receipt() {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // Status configuration
  const statusConfig = {
    SUCCESS: {
      color: "#2e7d32",
      icon: <CheckCircle sx={{ fontSize: 28 }} />,
      text: "Payment Successful",
    },
    PENDING: {
      color: "#ed6c02",
      icon: <Schedule sx={{ fontSize: 28 }} />,
      text: "Payment Pending",
    },
    FAILED: {
      color: "#d32f2f",
      icon: <Error sx={{ fontSize: 28 }} />,
      text: "Payment Failed",
    },
  };

  // Decode receipt data
  useEffect(() => {
    const param = new URLSearchParams(location.search).get("od");
    if (!param) {
      setError("No receipt data provided");
      return;
    }
    try {
      const obj = JSON.parse(atob(param));
      setData({
        ...obj,
        payment_amount: Number(obj.payment_amount || 0),
        convenience_fee: Number(obj.convenience_fee || 0),
        plan_price: obj.plan_price ? Number(obj.plan_price) : null,
      });
    } catch (e) {
      setError("Invalid receipt data");
    }
  }, [location.search]);

  const showSnackbar = (message) => setSnackbar({ open: true, message });

  // Download PDF
  const handlePdfDownload = async () => {
    if (!printRef.current) return;
    setBusy(true);

    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const ratio = Math.min(
        pdfWidth / (imgWidth * 0.264583),
        pdfHeight / (imgHeight * 0.264583)
      );
      const scaledWidth = imgWidth * 0.264583 * ratio;
      const scaledHeight = imgHeight * 0.264583 * ratio;
      const x = (pdfWidth - scaledWidth) / 2;
      const y = 10;

      pdf.addImage(img, "PNG", x, y, scaledWidth, scaledHeight);
      pdf.save(`Receipt_${data.order_id}.pdf`);
      showSnackbar("✓ PDF downloaded successfully");
    } catch (e) {
      showSnackbar("✗ Failed to generate PDF");
    } finally {
      setBusy(false);
    }
  };

  // Share to WhatsApp
  const shareToWhatsApp = async () => {
    if (!printRef.current) return;
    setBusy(true);

    try {
      const blob = await toBlob(printRef.current, {
        backgroundColor: "#fff",
        quality: 0.95,
        pixelRatio: 2,
      });

      if (!blob) throw new Error("Failed to generate image");

      const file = new File([blob], `Receipt_${data.order_id}.png`, {
        type: "image/png",
      });

      const shareText = `Payment Receipt\n\nAmount: ₹${data.payment_amount.toLocaleString("en-IN")}\nOrder ID: ${data.order_id}\nAccount: ${data.loan_number}\nStatus: ${data.payment_status}`;

      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Payment Receipt",
          text: shareText,
          files: [file],
        });
        showSnackbar("✓ Shared successfully");
      } else {
        const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
        window.open(whatsappUrl, "_blank");
        showSnackbar("Opening WhatsApp...");
      }
    } catch (e) {
      showSnackbar("✗ Failed to share");
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => navigate("/history", { replace: true });
  const formatCurrency = (amount) => `₹${amount.toLocaleString("en-IN")}`;
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Invalid Date";
    }
  };

  // Error state
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button onClick={goBack} variant="contained">
          Back to History
        </Button>
      </Container>
    );
  }

  // Loading state
  if (!data) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Loading receipt...
        </Typography>
      </Container>
    );
  }

  const status = statusConfig[data.payment_status] || statusConfig.PENDING;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        py: { xs: 3, sm: 6 },
      }}
    >
      <Container maxWidth="sm">
        {/* Receipt Paper */}
        <Paper
          ref={printRef}
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            bgcolor: "#fff",
            border: "1px solid #e0e0e0",
          }}
        >
          {/* Company Logo */}
          <Box textAlign="center" mb={4}>
            <CompanyLogo width="120px" />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            align="center"
            fontWeight="600"
            color="#1a1a1a"
            mb={1}
          >
            Payment Receipt
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            mb={2}
          >
            Transaction Confirmation
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Status */}
          <Box textAlign="center" mb={2}>
            <Box
              display="inline-flex"
              alignItems="center"
              gap={1}
              sx={{ color: status.color }}
            >
              {status.icon}
              <Typography variant="h6" fontWeight="600">
                {status.text}
              </Typography>
            </Box>
          </Box>

          {/* Amount */}
          <Box
            textAlign="center"
            mb={4}
            py={3}
            sx={{
              bgcolor: "#f8f9fa",
              borderRadius: 1,
              border: "1px solid #e9ecef",
            }}
          >
            <Typography variant="body2" color="text.secondary" mb={1}>
              Amount Paid
            </Typography>
            <Typography
              variant="h3"
              fontWeight="600"
              color={status.color}
              sx={{ letterSpacing: "-0.5px" }}
            >
              {formatCurrency(data.payment_amount)}
            </Typography>
          </Box>

          {/* Transaction Details */}
          <Typography
            variant="subtitle2"
            fontWeight="600"
            color="#1a1a1a"
            mb={2}
          >
            Transaction Details
          </Typography>

          <Grid container spacing={2} mb={4}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" display="block">
                Order ID
              </Typography>
              <Typography variant="body2" fontWeight="500" sx={{ wordBreak: "break-word" }}>
                {data.order_id}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" display="block">
                Receipt No.
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {data.reciept_no}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" display="block">
                Account Number
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {data.loan_number}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" display="block">
                Transaction Date
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {formatDate(data.payment_completion_time)}
              </Typography>
            </Grid>

            {data.convenience_fee > 0 && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Convenience Fee
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {formatCurrency(data.convenience_fee)}
                </Typography>
              </Grid>
            )}

            {data.plan_price != null && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Plan Price
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {formatCurrency(data.plan_price)}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Footer */}
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            display="block"
          >
            This is a computer-generated receipt and does not require a signature.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            display="block"
            mt={0.3}
          >
            Thank you for your payment!
          </Typography>
           <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            display="block"
            mt={0.3}
          >
            *********
          </Typography>
        </Paper>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          mt={3}
          mb={6}
        >
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handlePdfDownload}
            disabled={busy}
            fullWidth
            sx={{
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                borderColor: "#1565c0",
                bgcolor: "rgba(25, 118, 210, 0.04)",
              },
            }}
          >
            Download PDF
          </Button>

          <Button
            variant="outlined"
            startIcon={<WhatsApp />}
            onClick={shareToWhatsApp}
            disabled={busy}
            fullWidth
            sx={{
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#25d366",
              color: "#25d366",
              "&:hover": {
                borderColor: "#1da851",
                bgcolor: "rgba(37, 211, 102, 0.04)",
              },
            }}
          >
            Share
          </Button>

          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={goBack}
            fullWidth
            sx={{
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#1976d2",
              "&:hover": {
                bgcolor: "#1565c0",
              },
            }}
          >
            Back to History
          </Button>
        </Stack>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ open: false, message: "" })}
          message={snackbar.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Container>
    </Box>
  );
}
